import { getInternalApiInstance } from "./base";

// Gym Buddy operational controls. Mirrors the bws-internal admin adminOps service:
// a read-only inspector + preview/execute pairs for each remediation knob.

export type StranddedCategory =
    | "C1_zombie_active_group"
    | "C2_orphaned_buddy_entitlement"
    | "C3_group_killed_owner_paying"
    | "C4_seat_drift"
    | "C5_unclaimed_seat"
    | "C6_redundant_household_sub"
    | "healthy"
    | "offboarded"
    | "no_group";

export type MemberView = {
    userId: string;
    email: string;
    status: string;
    entitlementStatus: string | null;
    accessUntil: number | null;
    legacyProvisionEnd: number | null;
};

export type GroupView = {
    groupId: string;
    status: string;
    productId: string;
    maxSeats: number;
    currentSeats: number;
    ownerUserId: string;
    customerId: string;
    ownerSnapshotStatus: string | null;
    ownerLiveStripeStatus: string | null;
    ownerAccessUntil: number | null;
    members: MemberView[];
    category: StranddedCategory;
    diagnosis: string;
    recommended: string;
};

export type InspectResult = {
    query: string;
    resolvedUserId: string;
    email?: string;
    cognitoStatus: string | null;
    role: "owner" | "buddy" | "both" | "none";
    ownSubscriptions: {
        customerId: string;
        status: string | null;
        accessUntil: number | null;
    }[];
    groups: GroupView[];
};

export type PreviewBase = {
    blockers: string[];
    alerts: string[];
    canExecute: boolean;
};

export type AttachPreview = PreviewBase & {
    groupId: string;
    buddyUserId: string;
    buddyEmail?: string;
    seatsAvailable: number;
    buddyOwnActiveSubs: string[];
};

export type TeardownPreview = PreviewBase & {
    groupId: string;
    ownerSubLive: boolean;
    activeMembers: { userId: string; email: string }[];
    method: "cancel-subscription" | "direct-revoke";
};

const INSPECT = "/admin/subscription-group/inspect";
const base = "/admin/subscription-group";

export const inspectGymBuddy = async (query: string): Promise<InspectResult> => {
    const api = await getInternalApiInstance();
    const res = await api.get(INSPECT, { params: { query } });
    return res.data.data;
};

// --- attach buddy ---
export const previewAttachBuddy = async (body: {
    groupId: string;
    buddyUserId?: string;
    buddyEmail?: string;
}): Promise<AttachPreview> => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/attach-buddy/preview`, body);
    return res.data.data;
};
export const executeAttachBuddy = async (body: {
    groupId: string;
    buddyUserId?: string;
    buddyEmail?: string;
}) => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/attach-buddy/execute`, {
        ...body,
        confirm: true,
    });
    return res.data.data;
};

// --- teardown ---
export const previewTeardown = async (
    groupId: string
): Promise<TeardownPreview> => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/teardown/preview`, { groupId });
    return res.data.data;
};
export const executeTeardown = async (groupId: string) => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/teardown/execute`, {
        groupId,
        confirm: true,
    });
    return res.data.data;
};

// --- deactivate / reactivate ---
export const previewDeactivate = async (
    groupId: string
): Promise<PreviewBase & { groupId: string; ownerSubLive: boolean }> => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/deactivate/preview`, { groupId });
    return res.data.data;
};
export const executeDeactivate = async (groupId: string) => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/deactivate/execute`, {
        groupId,
        confirm: true,
    });
    return res.data.data;
};
export const previewReactivate = async (
    groupId: string
): Promise<PreviewBase & { groupId: string; ownerSubLive: boolean }> => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/reactivate/preview`, { groupId });
    return res.data.data;
};
export const executeReactivate = async (groupId: string) => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/reactivate/execute`, {
        groupId,
        confirm: true,
    });
    return res.data.data;
};

// --- force-remove member ---
export const previewRemoveMember = async (body: {
    groupId: string;
    memberUserId: string;
}): Promise<
    PreviewBase & { groupId: string; memberUserId: string; memberEmail?: string }
> => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/remove-member/preview`, body);
    return res.data.data;
};
export const executeRemoveMember = async (body: {
    groupId: string;
    memberUserId: string;
}) => {
    const api = await getInternalApiInstance();
    const res = await api.post(`${base}/remove-member/execute`, {
        ...body,
        confirm: true,
    });
    return res.data.data;
};
